/**
 * Admin wallet-adjust form → wire body. The user wallet is COIN-denominated,
 * so the operator types whole coins and they are sent untouched as
 * `amountCoins` — no ₹ and no coin-rate conversion.
 */
export type Direction = 'credit' | 'debit';

export interface WalletAdjustForm {
  direction: Direction;
  amountCoins: number;
  reason: string;
}

export interface WalletAdjustBody {
  direction: Direction;
  amountCoins: number;
  reason: string;
}

export const EMPTY_WALLET_ADJUST: WalletAdjustForm = {
  direction: 'credit',
  amountCoins: 0,
  reason: '',
};

export const MIN_ADJUST_REASON = 10;
export const MAX_ADJUST_REASON = 500;

/**
 * Most coins one admin CREDIT may add. Mirrors `ADMIN_CREDIT_MAX_COINS` in the
 * API, which enforces it independently — this copy is only for a clear message
 * before the request is sent.
 */
export const ADMIN_CREDIT_MAX_COINS = 500;

export function walletAdjustError(form: WalletAdjustForm): string | null {
  if (typeof form.amountCoins !== 'number' || !Number.isInteger(form.amountCoins) || form.amountCoins < 1)
    return 'Amount must be a whole number of coins ≥ 1';
  if (form.direction === 'credit' && form.amountCoins > ADMIN_CREDIT_MAX_COINS)
    return `Maximum ${ADMIN_CREDIT_MAX_COINS} coins per transaction`;
  const reason = form.reason.trim().length;
  if (reason < MIN_ADJUST_REASON) return `Reason needs at least ${MIN_ADJUST_REASON} characters`;
  if (reason > MAX_ADJUST_REASON) return `Reason must be at most ${MAX_ADJUST_REASON} characters`;
  return null;
}

/**
 * One key per adjustment the operator is making — minted when the dialog opens,
 * reused for every retry of that submission, replaced only after success. A
 * retry after a timeout (where the first request DID land) therefore replays
 * instead of crediting twice.
 */
export function newAdjustKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return `adm-${c.randomUUID()}`;
  return `adm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function walletAdjustBody(form: WalletAdjustForm): WalletAdjustBody {
  return {
    direction: form.direction,
    amountCoins: form.amountCoins,
    reason: form.reason.trim(),
  };
}
